import type { ReactNode } from 'react'
import { Icon } from '@iconify/react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  formatChartDay,
  formatCompactDate,
  formatCurrency,
  formatNumber,
  type DashboardInsights,
} from '@/lib/campaign-dashboard'
import type { CampaignSalesChart } from '@/types/campaign-report'
import styles from './CampaignDashboard.module.css'

interface CampaignDashboardChartProps {
  chart: CampaignSalesChart | null
  insights: DashboardInsights | null
  filters: ReactNode
  isLoading?: boolean
  error?: string | null
  onRetry?: () => void
}

type ChartSummaryItemProps = {
  label: string
  primary: string
  secondary?: string
}

function ChartSummaryItem({
  label,
  primary,
  secondary,
}: ChartSummaryItemProps) {
  return (
    <div className={styles.chartSummaryItem}>
      <span className={styles.kpiLabel}>{label}</span>
      <strong className={styles.chartSummaryValue}>{primary}</strong>
      {secondary && <span className={styles.chartSummarySubvalue}>{secondary}</span>}
    </div>
  )
}

function ChartBody({
  chart,
  insights,
}: {
  chart: CampaignSalesChart
  insights: DashboardInsights
}) {
  const totalAccumulated = chart.points.reduce((acc, point) => acc + point.amount, 0)

  return (
    <div className={styles.chartLayout}>
      <div className={styles.chartWrap}>
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={chart.points}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.24)" />
            <XAxis
              dataKey="date"
              tickFormatter={(value: string) => formatChartDay(value)}
              stroke="#64748b"
            />
            <YAxis
              yAxisId="left"
              stroke="#64748b"
              tickFormatter={(value: number) => formatCurrency(value)}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="#64748b"
              tickFormatter={(value: number) => formatNumber(value)}
            />
            <Tooltip
              formatter={(value, name) => {
                const numericValue = typeof value === 'number' ? value : Number(value ?? 0)
                if (name === 'Receita') return formatCurrency(numericValue)
                return formatNumber(numericValue)
              }}
              labelFormatter={(value) =>
                typeof value === 'string' ? formatCompactDate(value) : String(value ?? '')
              }
            />
            <Legend />
            <Bar
              yAxisId="right"
              dataKey="payments_count"
              fill="rgba(212, 0, 107, 0.45)"
              radius={[10, 10, 0, 0]}
              name="Pagamentos"
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="amount"
              stroke="#5e17eb"
              strokeWidth={3}
              dot={{ r: 3, fill: '#5e17eb' }}
              name="Receita"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className={styles.chartSummary}>
        <ChartSummaryItem
          label="Melhor dia"
          primary={
            insights.bestRevenueDay
              ? formatCompactDate(insights.bestRevenueDay.date)
              : '-'
          }
          secondary={
            insights.bestRevenueDay
              ? formatCurrency(insights.bestRevenueDay.amount)
              : undefined
          }
        />
        <ChartSummaryItem
          label="Pior dia"
          primary={
            insights.worstRevenueDay
              ? formatCompactDate(insights.worstRevenueDay.date)
              : '-'
          }
          secondary={
            insights.worstRevenueDay
              ? formatCurrency(insights.worstRevenueDay.amount)
              : undefined
          }
        />
        <ChartSummaryItem
          label="Pico de pagamentos"
          primary={
            insights.peakPaymentsDay
              ? formatCompactDate(insights.peakPaymentsDay.date)
              : '-'
          }
          secondary={
            insights.peakPaymentsDay
              ? `${formatNumber(insights.peakPaymentsDay.payments_count)} pagamentos`
              : undefined
          }
        />
        <ChartSummaryItem
          label="Receita acumulada"
          primary={formatCurrency(totalAccumulated)}
        />
      </div>
    </div>
  )
}

export function CampaignDashboardChart({
  chart,
  insights,
  filters,
  isLoading = false,
  error = null,
  onRetry,
}: CampaignDashboardChartProps) {
  const showChart = Boolean(chart && insights)

  return (
    <section className={styles.sectionCard} aria-labelledby="dashboard-performance-title">
      <div className={styles.chartHeading}>
        <div>
          <h2 id="dashboard-performance-title" className={styles.sectionTitle}>
            Performance temporal
          </h2>
          <p className={styles.sectionSubtitle}>
            Receita e quantidade de pagamentos por dia no intervalo selecionado.
          </p>
        </div>

        {filters}
      </div>

      {isLoading ? (
        <div className={styles.skeletonBlock} aria-hidden="true" />
      ) : showChart && chart && insights ? (
        <ChartBody chart={chart} insights={insights} />
      ) : (
        <div className={styles.chartState} role="alert">
          <p className={styles.stateText}>
            {error ||
              'A campanha não retornou dados suficientes para montar o gráfico.'}
          </p>
          {onRetry && (
            <button type="button" className={styles.actionButton} onClick={onRetry}>
              <Icon icon="mdi:refresh" />
              Recarregar dashboard
            </button>
          )}
        </div>
      )}
    </section>
  )
}
